import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Grid,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    CircularProgress,
    Tooltip,
    IconButton,
} from '@mui/material';
import { Check, Search, Info } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useGetJobTitlesQuery, useGetLocationManagersQuery } from '../../../../../api/preHQ';

const BasicDetails = ({ form }) => {
    const {
        watch,
        setValue,
        formState: { errors },
    } = form;
    const formValues = watch();

    const [deptIsOpen, setDeptIsOpen] = useState(false);
    const [deptSearch, setDeptSearch] = useState('');
    const [departments, setDepartments] = useState([]);
    const [jobTitleIsOpen, setJobTitleIsOpen] = useState(false);
    const [jobTitleSearch, setJobTitleSearch] = useState('');
    const [jobTitles, setJobTitles] = useState([]);
    const [locations, setLocations] = useState([]);
    const [locationIsOpen, setLocationIsOpen] = useState(false);
    const [locationSearch, setLocationSearch] = useState('');

    const deptDropdownRef = useRef(null);
    const jobTitleDropdownRef = useRef(null);
    const locationDropdownRef = useRef(null);

    const { data: jobTitleData, isLoading: isLoadingJobTitles } = useGetJobTitlesQuery({
        type: 'title',
        query: jobTitleSearch,
    });
    const { data: departmentData, isLoading: isLoadingDepartments } = useGetJobTitlesQuery({
        type: 'role',
        query: deptSearch,
    });
    const { data: locationManagerData, isLoading: isLoadingLocations } = useGetLocationManagersQuery();

    useEffect(() => {
        if (formValues.jobTitle) {
            setJobTitleSearch(formValues.jobTitle);
        }
        if (formValues.department) {
            setDeptSearch(formValues.department);
        }
        if (formValues.location?.label) {
            setLocationSearch(formValues.location.label || '');
        }
    }, [formValues.jobTitle, formValues.department, formValues.location?.label]);

    useEffect(() => {
        if (jobTitleData && jobTitleData.results) {
            // API returns an object with job titles as keys
            const formattedTitles = Object.keys(jobTitleData.results).map((title) => ({
                value: title,
                label: title,
            }));

            setJobTitles(formattedTitles);
        }
    }, [jobTitleData]);

    useEffect(() => {
        if (departmentData && departmentData.results) {
            // API returns an object with department names as keys
            const formattedDepartments = Object.keys(departmentData.results).map((dept) => ({
                value: dept,
                label: dept,
            }));

            setDepartments(formattedDepartments);
        }
    }, [departmentData]);
    useEffect(() => {
        if (deptSearch && departmentData?.results?.[deptSearch]) {
            const skills = departmentData.results[deptSearch];
            setValue('mandatorySkills', skills);
        }
    }, [deptSearch, departmentData, setValue]);

    useEffect(() => {
        if (locationManagerData && locationManagerData.locationsList) {
            const formattedLocations = locationManagerData.locationsList.map((location) => ({
                value: location.id,
                label: String(location.name || ''),
            }));
            setLocations(formattedLocations);
        }
    }, [locationManagerData]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (deptDropdownRef.current && !deptDropdownRef.current.contains(event.target)) {
                setDeptIsOpen(false);
            }
            if (jobTitleDropdownRef.current && !jobTitleDropdownRef.current.contains(event.target)) {
                setJobTitleIsOpen(false);
            }
            if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target)) {
                setTimeout(() => {
                    setLocationIsOpen(false);
                }, 100);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleJobTitleSelect = (title) => {
        setValue('jobTitle', title.value, { shouldValidate: true });
        setJobTitleIsOpen(false);
        setJobTitleSearch(title.label);

        // Auto-populate department and skills based on job title
        if (jobTitleData?.results && jobTitleData.results[title.value]) {
            const associatedDepartments = jobTitleData.results[title.value];
            if (associatedDepartments.length > 0) {
                setValue('department', associatedDepartments[0], { shouldValidate: true });
                setDeptSearch(associatedDepartments[0]);
            }
        }
    };

    const handleDepartmentSelect = (dept) => {
        setValue('department', dept.value, { shouldValidate: true });
        setDeptIsOpen(false);
        setDeptSearch(dept.label);

        if (departmentData?.results && departmentData.results[dept.value]) {
            const skills = departmentData.results[dept.value];
            setValue('mandatorySkills', skills);
        }
    };

    const handleLocationSelect = (location) => {
        setValue('location', { id: location.value, label: location.label }, { shouldValidate: true });
        setLocationIsOpen(false); // Re-added: Closes dropdown immediately on selection
        setLocationSearch(location.label);
    };
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 500 }}>
                Basic Details
            </Typography>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Box ref={jobTitleDropdownRef} sx={{ position: 'relative' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <TextField
                                    label="Position Title"
                                    placeholder="Search position titles..."
                                    value={jobTitleSearch}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setJobTitleSearch(value);
                                    }}
                                    onFocus={() => {
                                        setJobTitleIsOpen(true);
                                        if (formValues.jobTitle) {
                                            setJobTitleSearch('');
                                        }
                                    }}
                                    onBlur={() => {
                                        // Restore the previous value if the search input is empty and a job title was previously selected.
                                        if (!jobTitleSearch && formValues.jobTitle) {
                                            setJobTitleSearch(formValues.jobTitle);
                                        }
                                        // Delay closing the dropdown to allow click events on list items to register.
                                        setTimeout(() => {
                                            setJobTitleIsOpen(false);
                                        }, 100);
                                    }}
                                    error={!!errors.jobTitle}
                                    helperText={errors.jobTitle?.message}
                                    fullWidth
                                    size="small"
                                    InputProps={{
                                        endAdornment: isLoadingJobTitles ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <Search sx={{ color: 'grey.400' }} />
                                        ),
                                    }}
                                />
                                <Tooltip title="Job/Role Title" placement="top">
                                    <IconButton size="small" sx={{ p: 0.5, mt: 1 }}>
                                        <Info sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {jobTitleIsOpen && (
                                <Paper
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        zIndex: 1000,
                                        mt: 1,
                                        maxHeight: 240,
                                        overflow: 'auto',
                                        boxShadow: 3,
                                    }}
                                >
                                    <List dense>
                                        {jobTitles.length === 0 && jobTitleSearch ? (
                                            <ListItem
                                                button
                                                onClick={() =>
                                                    handleJobTitleSelect({
                                                        value: jobTitleSearch,
                                                        label: jobTitleSearch,
                                                    })
                                                }
                                            >
                                                <ListItemText primary={jobTitleSearch} />
                                            </ListItem>
                                        ) : jobTitles.length === 0 ? (
                                            <ListItem>
                                                <ListItemText
                                                    primary="No job titles found"
                                                    sx={{ color: 'text.secondary' }}
                                                />
                                            </ListItem>
                                        ) : (
                                            (() => {
                                                let displayJobTitles = [...jobTitles];
                                                if (formValues.jobTitle) {
                                                    const selectedItem = {
                                                        value: formValues.jobTitle,
                                                        label: formValues.jobTitle,
                                                    };
                                                    displayJobTitles = displayJobTitles.filter(
                                                        (item) => item.value !== selectedItem.value,
                                                    );
                                                    displayJobTitles.unshift(selectedItem);
                                                }
                                                return displayJobTitles.map((title) => (
                                                    <ListItem
                                                        key={title.value}
                                                        button
                                                        onMouseDown={() => handleJobTitleSelect(title)}
                                                        sx={{
                                                            bgcolor:
                                                                formValues.jobTitle === title.value
                                                                    ? 'action.selected'
                                                                    : 'transparent',
                                                        }}
                                                    >
                                                        <ListItemText primary={title.label} />
                                                        <ListItemIcon sx={{ minWidth: 0, marginLeft: 'auto' }}>
                                                            <Check
                                                                sx={{
                                                                    opacity:
                                                                        formValues.jobTitle === title.value ? 1 : 0,
                                                                    color: 'primary.main',
                                                                }}
                                                            />
                                                        </ListItemIcon>
                                                    </ListItem>
                                                ));
                                            })()
                                        )}
                                    </List>
                                </Paper>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box ref={deptDropdownRef} sx={{ position: 'relative' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <TextField
                                    label="Position Category"
                                    placeholder="Search from available categories..."
                                    value={deptSearch}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        setDeptSearch(value);
                                        setDeptIsOpen(true);
                                    }}
                                    onFocus={() => {
                                        setDeptIsOpen(true);
                                        if (formValues.department) {
                                            setDeptSearch('');
                                        }
                                    }}
                                    onBlur={() => {
                                        if (!deptSearch && formValues.department) {
                                            setDeptSearch(formValues.department);
                                        }
                                        setTimeout(() => {
                                            setDeptIsOpen(false);
                                        }, 100);
                                    }}
                                    size="small"
                                    error={!!errors.department}
                                    helperText={errors.department?.message}
                                    fullWidth
                                    InputProps={{
                                        endAdornment: isLoadingDepartments ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            <Search sx={{ color: 'grey.400' }} />
                                        ),
                                    }}
                                />
                                <Tooltip title="Role category" placement="top">
                                    <IconButton size="small" sx={{ p: 0.5, mt: 1 }}>
                                        <Info sx={{ fontSize: 18, color: 'text.secondary' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            {deptIsOpen && (
                                <Paper
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        zIndex: 1000,
                                        mt: 1,
                                        maxHeight: 240,
                                        overflow: 'auto',
                                        boxShadow: 3,
                                    }}
                                >
                                    <List dense>
                                        {departments.length === 0 ? (
                                            <ListItem>
                                                <ListItemText
                                                    primary="No roles found"
                                                    sx={{ color: 'text.secondary' }}
                                                />
                                            </ListItem>
                                        ) : (
                                            (() => {
                                                let displayDepartments = [...departments];
                                                if (formValues.department) {
                                                    const selectedDepartment = {
                                                        value: formValues.department,
                                                        label: formValues.department,
                                                    };
                                                    displayDepartments = displayDepartments.filter(
                                                        (dept) => dept.value !== selectedDepartment.value,
                                                    );
                                                    displayDepartments.unshift(selectedDepartment);
                                                }
                                                return displayDepartments.map((dept) => (
                                                    <ListItem
                                                        key={dept.value}
                                                        button
                                                        onMouseDown={() => handleDepartmentSelect(dept)}
                                                        sx={{
                                                            bgcolor:
                                                                formValues.department === dept.value
                                                                    ? 'action.selected'
                                                                    : 'transparent',
                                                        }}
                                                    >
                                                        <ListItemText primary={dept.label} />
                                                        <ListItemIcon sx={{ minWidth: 0, marginLeft: 'auto' }}>
                                                            <Check
                                                                sx={{
                                                                    opacity:
                                                                        formValues.department === dept.value ? 1 : 0,
                                                                    color: 'primary.main',
                                                                }}
                                                            />
                                                        </ListItemIcon>
                                                    </ListItem>
                                                ));
                                            })()
                                        )}
                                    </List>
                                </Paper>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box ref={locationDropdownRef} sx={{ position: 'relative' }}>
                            <TextField
                                label="Work Location"
                                placeholder="Search locations..."
                                value={locationSearch}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setLocationSearch(value);
                                }}
                                onFocus={() => {
                                    setLocationIsOpen(true);
                                    if (formValues.location?.label) {
                                        setLocationSearch('');
                                    }
                                }}
                                onBlur={() => {
                                    if (!locationSearch && formValues.location?.label) {
                                        setLocationSearch(formValues.location.label || '');
                                    }
                                    setTimeout(() => {
                                        setLocationIsOpen(false);
                                    }, 100);
                                }}
                                error={!!errors.location}
                                helperText={errors.location?.message}
                                fullWidth
                                size="small"
                                InputProps={{
                                    endAdornment: isLoadingLocations ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <Search sx={{ color: 'grey.400' }} />
                                    ),
                                }}
                            />

                            {locationIsOpen && (
                                <Paper
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        zIndex: 1000,
                                        mt: 1,
                                        maxHeight: 240,
                                        overflow: 'auto',
                                        boxShadow: 3,
                                    }}
                                >
                                    <List dense>
                                        {locations.length === 0 ? (
                                            <ListItem>
                                                <ListItemText
                                                    primary="No locations found"
                                                    sx={{ color: 'text.secondary' }}
                                                />
                                            </ListItem>
                                        ) : (
                                            (() => {
                                                let displayLocations = [...locations];
                                                if (formValues.location?.id) {
                                                    const selectedLocation = {
                                                        value: formValues.location.id,
                                                        label: formValues.location.label,
                                                    };
                                                    displayLocations = displayLocations.filter(
                                                        (loc) => loc.value !== selectedLocation.value,
                                                    );
                                                    displayLocations.unshift(selectedLocation);
                                                }
                                                return displayLocations
                                                    .filter(
                                                        (loc) =>
                                                            !locationSearch ||
                                                            (loc.label || '')
                                                                .toLowerCase()
                                                                .includes(locationSearch.toLowerCase()),
                                                    )
                                                    .map((location) => (
                                                        <ListItem
                                                            key={location.value}
                                                            button
                                                            onMouseDown={() => handleLocationSelect(location)}
                                                            sx={{
                                                                bgcolor:
                                                                    formValues.location?.id === location.value
                                                                        ? 'action.selected'
                                                                        : 'transparent',
                                                            }}
                                                        >
                                                            <ListItemText primary={location.label} />
                                                            <ListItemIcon sx={{ minWidth: 0, marginLeft: 'auto' }}>
                                                                <Check
                                                                    sx={{
                                                                        opacity:
                                                                            formValues.location?.id === location.value
                                                                                ? 1
                                                                                : 0,
                                                                        color: 'primary.main',
                                                                    }}
                                                                />
                                                            </ListItemIcon>
                                                        </ListItem>
                                                    ));
                                            })()
                                        )}
                                    </List>
                                </Paper>
                            )}
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Minimum Experience (Years)"
                            type="number"
                            min={0}
                            placeholder="0"
                            value={formValues.minYearsExp || ''}
                            onChange={(e) =>
                                setValue('minYearsExp', parseInt(e.target.value) || 0, { shouldValidate: true })
                            }
                            error={!!errors.minYearsExp}
                            helperText={errors.minYearsExp?.message}
                            fullWidth
                            size="small"
                            sx={{ width: '90%' }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            label="Maximum Experience (Years)"
                            type="number"
                            min={0}
                            placeholder="0"
                            value={formValues.maxYearsExp || ''}
                            onChange={(e) =>
                                setValue('maxYearsExp', parseInt(e.target.value) || 0, { shouldValidate: true })
                            }
                            error={!!errors.maxYearsExp}
                            helperText={errors.maxYearsExp?.message}
                            fullWidth
                            size="small"
                            sx={{ width: '90%' }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            label="No. Of Positions"
                            type="number"
                            min={1}
                            placeholder="1"
                            value={formValues.noOfPositions || ''}
                            onChange={(e) =>
                                setValue('noOfPositions', parseInt(e.target.value) || 1, { shouldValidate: true })
                            }
                            error={!!errors.noOfPositions}
                            helperText={errors.noOfPositions?.message}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </Box>
    );
};

export default BasicDetails;
